import { useEffect, useState } from "react";
import { Loader2, ArrowDownToLine, ArrowUpFromLine, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui-kit/Card";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";
import { PaymentStatusPill } from "./PaymentStatusPill";
import { PaymentDialog } from "./PaymentDialog";

function ReceiveForm({ clientId, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!(Number(amount) > 0)) return setError("Enter an amount greater than 0.");
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/retainer/received`, { amount: Number(amount), date, reference, note });
      onDone?.();
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <DialogTitle className="flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-md bg-status-paid/10 text-status-paid"><ArrowDownToLine className="h-4 w-4" /></span>
        Record retainer received
      </DialogTitle>
      <DialogDescription>Increases the client&apos;s retainer balance. Apply it later to reduce a matter&apos;s amount due.</DialogDescription>
      <div className="mt-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ret-amount">Amount ($)</Label>
            <Input id="ret-amount" data-testid="retainer-amount-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <Label htmlFor="ret-date">Date received</Label>
            <Input id="ret-date" data-testid="retainer-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <div>
          <Label htmlFor="ret-ref">Reference</Label>
          <Input id="ret-ref" data-testid="retainer-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="retainer-error">{error}</p>}
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="retainer-receive-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownToLine className="h-4 w-4" />} Record received
        </Button>
      </DialogFooter>
    </>
  );
}

function ReceiveDialog({ open, onOpenChange, clientId, onDone }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="retainer-receive-dialog">
        <ReceiveForm clientId={clientId} onClose={() => onOpenChange(false)} onDone={onDone} />
      </DialogContent>
    </Dialog>
  );
}

/** Client-level retainer: balance + transaction history + record/apply actions. */
export function RetainerCard({ clientId }) {
  const [data, setData] = useState(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const load = () => api.get(`/clients/${clientId}/retainer`).then((r) => setData(r.data)).catch(() => setData({ balance: 0, transactions: [] }));
  useEffect(() => { load(); }, [clientId]);

  return (
    <Card data-testid="retainer-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Retainer</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setReceiveOpen(true)} data-testid="retainer-record-received">
            <ArrowDownToLine className="h-4 w-4" /> Record received
          </Button>
          <Button size="sm" onClick={() => setApplyOpen(true)} data-testid="retainer-apply-open">
            <ArrowUpFromLine className="h-4 w-4" /> Apply to matter
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {data === null ? (
          <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Available balance</p>
              <p className="nums text-2xl font-semibold text-foreground" data-testid="retainer-balance">{money(data.balance)}</p>
            </div>
            {data.transactions.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No retainer activity yet.</p>
            ) : (
              <div className="space-y-1.5">
                {data.transactions.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm" data-testid={`retainer-txn-${t.id}`}>
                    {t.type === "received"
                      ? <ArrowDownToLine className="h-4 w-4 shrink-0 text-status-paid" />
                      : <ArrowUpFromLine className="h-4 w-4 shrink-0 text-primary" />}
                    <span className="text-foreground">{t.type === "received" ? "Received" : `Applied → ${t.matter_name || "matter"}`}</span>
                    <span className="text-muted-foreground">{shortDate(t.date)}</span>
                    {t.reference && <span className="text-xs text-muted-foreground">· {t.reference}</span>}
                    <span className={`nums ml-auto font-medium ${t.type === "received" ? "text-status-paid" : "text-foreground"}`}>
                      {t.type === "received" ? "+" : "−"}{money(t.amount)}
                    </span>
                    <PaymentStatusPill status={t.status} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <ReceiveDialog open={receiveOpen} onOpenChange={setReceiveOpen} clientId={clientId} onDone={load} />
      <PaymentDialog
        open={applyOpen} onOpenChange={setApplyOpen} clientId={clientId} lockMethod="Retainer"
        title="Apply retainer to a matter" onDone={load}
      />
    </Card>
  );
}
